import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";

const MAX_WORDS = 500;

const PARTS = [
  { k: "story", label: "Quran story", prompt: "Retell the story in your own words." },
  { k: "lesson", label: "Life lesson", prompt: "Write what this story taught you for your own life." },
];

const countWords = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

export default function ReflectionPanel({ seq }) {
  const [texts, setTexts] = useState({ story: "", lesson: "" });
  const [part, setPart] = useState("story");
  const [savedAt, setSavedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoaded(false);
    client.get(`/stories/${seq}/reflection`)
      .then(({ data }) => {
        setTexts({ story: data.reflection?.story || "", lesson: data.reflection?.lesson || "" });
        setSavedAt(data.reflection?.updated_at || null);
      })
      .catch((ex) => setErr(formatApiError(ex.response?.data?.detail) || ex.message))
      .finally(() => setLoaded(true));
  }, [seq]);

  const current = PARTS.find((p) => p.k === part);
  const words = countWords(texts[part]);
  const overLimit = words > MAX_WORDS;
  const anyText = texts.story.trim() || texts.lesson.trim();

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      const { data } = await client.put(`/stories/${seq}/reflection`, texts);
      setSavedAt(data.updated_at);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="mt-6 text-hv-slate">Loading…</div>;

  return (
    <div className="mt-6" data-testid="story-reflection">
      <div className="flex gap-2 flex-wrap" data-testid="reflection-sub-tabs">
        {PARTS.map((p) => (
          <button key={p.k} type="button" onClick={() => setPart(p.k)} data-testid={`reflection-tab-${p.k}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition ${
                    part === p.k ? "bg-hv-teal text-white" : "bg-white text-hv-navy border-2 border-[#E5DFC8]"
                  }`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="mt-4 hv-card p-6">
        <div className="hv-heading text-hv-navy text-xl">{current.label}</div>
        <p className="mt-2 text-hv-slate text-sm">{current.prompt} Up to {MAX_WORDS} words.</p>
        <textarea
          value={texts[part]}
          onChange={(e) => setTexts((t) => ({ ...t, [part]: e.target.value }))}
          rows={10}
          className="mt-4 w-full rounded-2xl border-2 border-[#E5DFC8] bg-white p-4 text-hv-navy focus:border-hv-teal focus:outline-none"
          data-testid={`reflection-text-${part}`}
        />
        <div className="mt-2 flex items-center justify-between text-sm">
          <span className={overLimit ? "font-bold text-hv-coral" : "text-hv-slate"} data-testid="reflection-count">
            {words} / {MAX_WORDS} words
          </span>
          {savedAt && <span className="text-hv-slate">Saved</span>}
        </div>
        {err && <div className="mt-3 text-hv-coral font-bold" data-testid="reflection-error">{err}</div>}
        <button type="button" onClick={save} disabled={saving || overLimit || !anyText}
                className="hv-btn-primary mt-4 disabled:opacity-50" data-testid="reflection-save">
          {saving ? "Saving…" : "Save reflection"}
        </button>
      </div>
    </div>
  );
}
